import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AnimateIn from "./AnimateIn";
import WCButton from "./WCButton";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EmailSignup = ({
  heading = "NEVER MISS A DROP",
  body = "WrongConception drops are limited. When they are gone they are gone. Get first access before anyone else. No spam. Just drops. Just stories. Just WrongConception.",
  source = "homepage",
  fullHeight = true,
  testidPrefix = "signup",
  showCheckboxes = true,
}) => {
  const [email, setEmail] = useState("");
  const [international, setInternational] = useState(true);
  const [domestic, setDomestic] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email,
        international,
        domestic,
        source,
      });
      toast.success("You are on the list. Welcome to WrongConception.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className={`bg-[#111111] px-6 md:px-12 ${
        fullHeight ? "min-h-screen md:min-h-[80vh]" : "py-28"
      } flex items-center`}
      data-testid={`${testidPrefix}-section`}
    >
      <div className="max-w-3xl mx-auto w-full">
        <AnimateIn variant="slam">
          <h2 className="wc-heading text-white text-5xl md:text-7xl tracking-widest leading-none">
            {heading}
          </h2>
        </AnimateIn>
        <AnimateIn variant="fade" delay={200}>
          <p className="text-white/75 text-base md:text-lg mt-8 leading-relaxed max-w-2xl">
            {body}
          </p>
        </AnimateIn>

        <form onSubmit={submit} className="mt-12 space-y-6">
          {showCheckboxes && (
            <AnimateIn variant="fade" delay={300}>
              <div className="flex flex-col md:flex-row gap-5 md:gap-10 wc-label text-xs text-white">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={international}
                    onChange={(e) => setInternational(e.target.checked)}
                    className="w-4 h-4 accent-white bg-black border border-white"
                    data-testid={`${testidPrefix}-check-international`}
                  />
                  <span>International Drops</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={domestic}
                    onChange={(e) => setDomestic(e.target.checked)}
                    className="w-4 h-4 accent-white bg-black border border-white"
                    data-testid={`${testidPrefix}-check-domestic`}
                  />
                  <span>Domestic Drops</span>
                </label>
                <span className="text-white/50 normal-case tracking-normal">
                  Both recommended.
                </span>
              </div>
            </AnimateIn>
          )}

          <AnimateIn variant="fade" delay={400}>
            <input
              type="email"
              required
              placeholder="YOUR EMAIL ADDRESS"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border border-white/50 focus:border-white text-white placeholder:text-white/40 wc-label text-sm px-5 py-5 outline-none transition-colors"
              data-testid={`${testidPrefix}-email-input`}
            />
          </AnimateIn>

          <AnimateIn variant="fade" delay={500}>
            <WCButton
              type="submit"
              filled
              disabled={loading}
              data-testid={`${testidPrefix}-submit`}
              className="w-full md:w-auto"
            >
              <span className="inline-flex items-center gap-3">
                {loading ? "JOINING..." : "GET FIRST ACCESS"}
                <ArrowRight size={16} />
              </span>
            </WCButton>
            <p className="text-white/40 text-xs mt-5 wc-label">
              No spam. Just drops. Just WrongConception.
            </p>
          </AnimateIn>
        </form>
      </div>
    </section>
  );
};

export default EmailSignup;
